import type {
  InjectionProtocol,
  ProtocolEscalation,
  ProtocolStep,
} from "./types";

/**
 * The session that walks a patient through a protocol.
 *
 * A pure reducer on purpose. #2267 requires that a general-purpose model never
 * decide whether an injection is safe or has happened, and the way to guarantee
 * that is to keep the decision somewhere the model cannot reach: position in the
 * procedure, whether the patient has confirmed, and whether the flow is paused are
 * all computed here from events, never inferred from what was said. The realtime
 * layer (#2276) will phrase `currentStep`'s copy and report events back; it does
 * not get a vote on what the next step is.
 *
 * No I/O, no clock, no randomness — so the whole safety contract is testable
 * without a vendor, a camera, or a patient.
 */

export type SessionStatus =
  /** Working through the steps. */
  | "in-progress"
  /** Held on the current step; only `resume` or `abort` move it. */
  | "paused"
  /** Every step done. */
  | "complete"
  /** Ended before the end. Terminal. */
  | "aborted";

export type SessionState = {
  protocol: InjectionProtocol;
  stepIndex: number;
  status: SessionStatus;
  /**
   * Whether the patient has confirmed she is ready for the step she is on. Reset
   * on every move, so a confirmation can never carry across steps — the thing
   * confirmed was this step, not the procedure.
   */
  confirmedCurrentStep: boolean;
  /**
   * The warning that paused the session, if one did. Carries the escalation the
   * caller must act on.
   */
  raisedWarning: { id: string; escalation: ProtocolEscalation } | null;
};

export type SessionEvent =
  /** The patient says she is ready for the current step. */
  | { type: "confirm" }
  /** Move to the next step. Blocked when the current step needs confirming. */
  | { type: "advance" }
  | { type: "back" }
  | { type: "pause" }
  | { type: "resume" }
  /** A warning fired — by the patient, the clinician, or a perception signal. */
  | { type: "raise-warning"; warningId: string }
  | { type: "abort" };

export function startSession(protocol: InjectionProtocol): SessionState {
  return {
    protocol,
    stepIndex: 0,
    status: "in-progress",
    confirmedCurrentStep: false,
    raisedWarning: null,
  };
}

export function currentStep(state: SessionState): ProtocolStep | null {
  return state.protocol.steps[state.stepIndex] ?? null;
}

/**
 * Whether the session may leave the current step.
 *
 * Exported because the UI needs the same answer the reducer uses — a "Continue"
 * button that looks pressable and then does nothing is worse than one that is
 * plainly disabled, and computing the rule twice invites the two to disagree.
 */
export function canAdvance(state: SessionState): boolean {
  if (state.status !== "in-progress") return false;
  const step = currentStep(state);
  if (!step) return false;
  return !step.requiresConfirmation || state.confirmedCurrentStep;
}

/**
 * Whether the session may return to the previous step.
 *
 * Exported for the same reason as `canAdvance`, and it was missed: the walk
 * rendered "Previous step" on every step past the first, including the aftercare
 * step, where `back` is refused because the step behind it is the injection. A
 * visible, enabled button that does nothing — immediately after the one moment
 * that cannot be undone.
 */
export function canGoBack(state: SessionState): boolean {
  if (state.status !== "in-progress") return false;
  if (state.stepIndex === 0) return false;
  return state.protocol.steps[state.stepIndex - 1]?.kind !== "irreversible";
}

function findWarning(state: SessionState, warningId: string) {
  for (const step of state.protocol.steps) {
    const hit = step.warnings?.find((warning) => warning.id === warningId);
    if (hit) return hit;
  }
  return null;
}

export function reduce(state: SessionState, event: SessionEvent): SessionState {
  // Aborting is always available and always final. It is the patient's way out of
  // a procedure she does not want to be in, so nothing gates it — not a pause,
  // not an unconfirmed step, not a raised warning.
  if (event.type === "abort") {
    return { ...state, status: "aborted", raisedWarning: null };
  }

  if (state.status === "aborted" || state.status === "complete") return state;

  // A warning halts wherever it lands. Deliberately reachable while paused: a
  // second, worse symptom during a pause must still be able to change the
  // escalation the caller is acting on.
  if (event.type === "raise-warning") {
    const warning = findWarning(state, event.warningId);
    if (!warning) return state;
    if (warning.severity === "caution") return state;
    return {
      ...state,
      status: "paused",
      raisedWarning: { id: warning.id, escalation: warning.escalation },
    };
  }

  if (state.status === "paused") {
    if (event.type === "resume") {
      return { ...state, status: "in-progress", raisedWarning: null };
    }
    // Everything else is ignored while paused, including `confirm`. A patient who
    // has just been told to call her clinic must not be able to confirm her way
    // past the injection without first clearing the reason she was stopped.
    return state;
  }

  switch (event.type) {
    case "confirm":
      return { ...state, confirmedCurrentStep: true };

    case "advance": {
      if (!canAdvance(state)) return state;
      const nextIndex = state.stepIndex + 1;
      if (nextIndex >= state.protocol.steps.length) {
        return { ...state, status: "complete", confirmedCurrentStep: false };
      }
      return {
        ...state,
        stepIndex: nextIndex,
        confirmedCurrentStep: false,
      };
    }

    case "back": {
      if (state.stepIndex === 0) return state;
      // Going back past an injection is not a navigation problem — the dose is
      // delivered and the step is not repeatable. The session stays put; recovery
      // belongs with the clinic, which is what the spill warning routes to.
      if (state.protocol.steps[state.stepIndex - 1]?.kind === "irreversible") {
        return state;
      }
      return {
        ...state,
        stepIndex: state.stepIndex - 1,
        confirmedCurrentStep: false,
      };
    }

    case "pause":
      return { ...state, status: "paused" };

    default:
      return state;
  }
}
